import { Button } from "@/components/ui/button";
import CreateSupplier from "@/v2/api/supplier/Create";
import {
    ArForm,
    ArFormField,
    ArTextAreaFormField,
} from "@/v2/components/ui/form";
import { CreateSupplierSchema, SupplierFormSchema } from "@/v2/schema/supplier";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import FindOneSupplier from "@/v2/api/supplier/FindOne";
import UpdateSupplier from "@/v2/api/supplier/Update";
import { useEffect } from "react";
import OnboardSupplier from "@/v2/api/supplier/Onboard";
import LoadingAnimation from "../../ui/LoadingAnimation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArUserCombobox } from "../../ui/combo-box/ArUser";

export default function SupplierForm({
    id,
    token,
    setSubmitted,
}: {
    id?: string;
    token?: string;
    setSubmitted?: (value: boolean) => void;
}) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const query = useQuery({
        queryKey: ["suppliers", id],
        queryFn: () => FindOneSupplier(id!),
        enabled: !!id,
    });

    const form = useForm<SupplierFormSchema>({
        resolver: zodResolver(SupplierFormSchema),
        defaultValues: {
            email: "",
            full_name: "",
            full_name_ar: "",
            short_code: "",
            locations: [
                {
                    value: "",
                },
            ],
            phone_number: "",
            cr_number: "",
            vat_number: "",
            iban: "",
            bank_name: "",
            account_number: "",
            representatives: [
                {
                    id: null,
                    email: "",
                    full_name: "",
                    phone_number: "",
                },
            ],
            website: "",
            assigned_supplier_ids: null,
            company_address: "",
            address_ar: "",
        },
    });

    const { reset } = form;

    useEffect(() => {
        if (query.data && !form.formState.isDirty) {
            reset({
                email: query.data.email ?? "",
                full_name: query.data.full_name ?? "",
                full_name_ar: query.data.full_name_ar ?? "",
                short_code: query.data.short_code ?? "",
                // vendor_code: query.data.vendor_code ?? "",
                locations: query.data.locations?.map((loc: string) => ({
                    value: loc,
                })) ?? [{ value: "" }],
                phone_number: query.data.phone_number ?? "",
                cr_number: query.data.cr_number ?? "",
                vat_number: query.data.vat_number ?? "",
                iban: query.data.iban ?? "",
                account_number: query.data.account_number ?? "",
                bank_name: query.data.bank_name ?? "",
                company_address: query.data.company_address ?? "",
                address_ar: query?.data?.address_ar ?? "",
                representatives: query.data.representatives?.map(
                    (rep: any) => ({
                        id: rep.id ?? null,
                        email: rep.email ?? "",
                        full_name: rep.full_name ?? "",
                        phone_number: rep.phone_number ?? "",
                    }),
                ) ?? [
                    {
                        id: null,
                        email: "",
                        full_name: "",
                        phone_number: "",
                    },
                ],
                website: query.data.website ?? "",
                assigned_supplier_ids:
                    query?.data?.assigned_supplier?.length !== 0
                        ? [query.data.assigned_supplier?.[0]?.id]
                        : null,
            });
        }
    }, [query.data, reset, form.formState.isDirty]);

    const {
        fields: representativesFields,
        append: representativesAppend,
        remove: representativesRemove,
    } = useFieldArray({
        control: form.control,
        name: "representatives",
    });

    const {
        fields: locationsFields,
        append: locationsAppend,
        remove: locationsRemove,
    } = useFieldArray({
        control: form.control,
        name: "locations",
    });

    const CreateMutation = useMutation({
        mutationFn: (data: CreateSupplierSchema) => CreateSupplier(data),
    });

    const EditMutation = useMutation({
        mutationFn: (data: CreateSupplierSchema) =>
            UpdateSupplier({ id: id!, data }),
    });

    const OnboardMutation = useMutation({
        mutationFn: (data: CreateSupplierSchema) =>
            OnboardSupplier({ token: token!, data }),
    });

    const handleFormSubmit = (data: SupplierFormSchema) => {
        const mutation = token
            ? OnboardMutation
            : id
              ? EditMutation
              : CreateMutation;

        const action = token ? "onboard" : id ? "updated" : "created";

        const transformedData = {
            ...data,
            locations: data.locations.map((item) => item.value),
        };

        mutation.mutate(transformedData, {
            onError: (err) => {
                console.error({ err });
                toast.error("Oops! Something went wrong", {
                    description: `While ${id ? "updating" : "creating"} the supplier, we encountered an issue.`,
                });
            },
            onSuccess: (res) => {
                toast.success(`Supplier ${action} successfully`);
                if (!token) navigate(`/supplier-database/${res.id}`);
                setSubmitted && setSubmitted(true);
                queryClient.invalidateQueries({
                    queryKey: ["suppliers"],
                });
            },
        });
    };

    if (id && query.isLoading) {
        return <LoadingAnimation />;
    }

    if (id && query.isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">Failed to load supplier</p>
                </div>
            </div>
        );
    }

    return (
        <ArForm
            form={form}
            formSchema={SupplierFormSchema}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-6">
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Basic Information</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArFormField<SupplierFormSchema>
                            label="Full Name"
                            name="full_name"
                            placeholder="Enter full name"
                        />
                        <ArFormField<SupplierFormSchema>
                            label="Full Name (Arabic)"
                            name="full_name_ar"
                            lang="ar"
                            dir="rtl"
                            labelAlign="items-end"
                            placeholder="Enter full name in arabic"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArFormField<SupplierFormSchema>
                            label="Short Code"
                            name="short_code"
                            placeholder="Enter short code"
                        />

                        <ArFormField<SupplierFormSchema>
                            label="Email"
                            name="email"
                            placeholder="Enter email"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArFormField<SupplierFormSchema>
                            label="Phone Number"
                            name="phone_number"
                            placeholder="Enter phone number"
                        />

                        <ArFormField<SupplierFormSchema>
                            label="Website"
                            name="website"
                            placeholder="Enter website URL"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArTextAreaFormField<SupplierFormSchema>
                            label="Address"
                            name="company_address"
                            placeholder="Enter address"
                        />

                        <ArTextAreaFormField<SupplierFormSchema>
                            label="Address (arabic)"
                            name="address_ar"
                            lang="ar"
                            dir="rtl"
                            labelAlign="items-end"
                            className="font-arabic"
                            rows={4}
                            placeholder="Enter address (arabic)"
                        />
                    </div>

                    {!token && (
                        <>
                            <h3 className="text-lg font-semibold">
                                Assigned AR User
                            </h3>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <ArUserCombobox<SupplierFormSchema>
                                    name="assigned_supplier_ids"
                                    defaultValueId={
                                        query?.data?.assigned_supplier?.[0]?.id
                                    }
                                    defaultValue={
                                        query?.data?.assigned_supplier?.[0] ||
                                        null
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Registration Details */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">
                        Registration Details
                    </h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArFormField<SupplierFormSchema>
                            label="CR Number"
                            name="cr_number"
                            placeholder="Enter commercial registration number"
                        />

                        <ArFormField<SupplierFormSchema>
                            label="VAT Number"
                            name="vat_number"
                            placeholder="Enter VAT number"
                        />
                    </div>
                </div>
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Bank Details</h3>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ArFormField<SupplierFormSchema>
                            label="IBAN Number"
                            name="iban"
                            placeholder="Enter IBAN number"
                        />

                        <ArFormField<SupplierFormSchema>
                            label="Bank Name"
                            name="bank_name"
                            placeholder="Enter Bank name"
                        />
                    </div>
                    <ArFormField<SupplierFormSchema>
                        label="Account Number"
                        name="account_number"
                        placeholder="Enter account number"
                    />
                </div>
                <div className="space-y-4">
                    <div className="space-y-4 rounded-lg border p-4">
                        <div className="flex flex-col items-center justify-between md:flex-row">
                            <h3 className="text-lg font-semibold">Locations</h3>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => locationsAppend({ value: "" })}
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Add Location
                            </Button>
                        </div>

                        <div className="space-y-3">
                            {locationsFields.map((field, index) => (
                                <div
                                    key={field.id}
                                    className="flex items-center gap-2"
                                >
                                    <div className="flex-1">
                                        <ArFormField<SupplierFormSchema>
                                            label={`Location ${index + 1}`}
                                            name={`locations.${index}.value`}
                                            placeholder="Enter location"
                                        />
                                    </div>
                                    {locationsFields.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                                locationsRemove(index)
                                            }
                                            className="mt-8" // Align with input field (accounting for label height)
                                        >
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                {/* Representatives */}
                <div className="space-y-4">
                    <div className="flex flex-col items-center justify-between md:flex-row">
                        <h3 className="text-lg font-semibold">
                            Representatives
                        </h3>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                representativesAppend({
                                    id: null,
                                    email: "",
                                    full_name: "",
                                    phone_number: "",
                                })
                            }
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Representative
                        </Button>
                    </div>

                    {representativesFields.map((field, index) => (
                        <div
                            key={field.id}
                            className="space-y-4 rounded-lg border p-4"
                        >
                            <div className="flex items-center justify-between">
                                <h4 className="font-medium">
                                    Representative {index + 1}
                                </h4>
                                {representativesFields.length > 1 && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                            representativesRemove(index)
                                        }
                                    >
                                        <Trash2 className="h-4 w-4 text-red-600" />
                                    </Button>
                                )}
                            </div>

                            <ArFormField<SupplierFormSchema>
                                label="Full Name"
                                name={`representatives.${index}.full_name`}
                                placeholder="Enter representative name"
                            />

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <ArFormField<SupplierFormSchema>
                                    label="Email"
                                    name={`representatives.${index}.email`}
                                    placeholder="Enter email"
                                />

                                <ArFormField<SupplierFormSchema>
                                    label="Phone Number"
                                    name={`representatives.${index}.phone_number`}
                                    placeholder="Enter phone number"
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            CreateMutation.isPending || EditMutation.isPending
                        }
                    >
                        {CreateMutation.isPending || EditMutation.isPending
                            ? "Saving..."
                            : id
                              ? "Update Supplier"
                              : "Create Supplier"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
